import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Check, ArrowRight } from "lucide-react";
import { MaskedLine, EASE } from "../lib/motion";

const PROOF_POINTS = [
  "Senior CFO Expertise",
  "AI & Automation",
  "Global Experience",
  "Flexible Engagement",
];

export const Hero = ({ onConsult }) => {
  const photoRef = useRef(null);
  const [isDesktop, setIsDesktop] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);

  const { scrollYProgress } = useScroll({
    target: photoRef,
    offset: ["start end", "end start"],
  });
  const yPhoto = useTransform(scrollYProgress, [0, 1], [40, -40]);
  const yShape = useTransform(scrollYProgress, [0, 1], [-30, 60]);

  return (
    <section id="home" data-testid="hero-section" className="relative hero-grid-bg overflow-hidden pt-[72px]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-[1.15fr_0.85fr] gap-16 items-center min-h-[calc(100vh-72px)] py-20">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.15, ease: EASE }}
            className="mb-7 inline-flex items-center gap-2.5 rounded-full border border-border bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.22em] text-navy/70"
          >
            <span className="h-1.5 w-1.5 rounded-full bg-brand" />
            Virtual & Fractional CFO Practice
          </motion.p>

          <h1 data-testid="hero-headline" className="font-display font-extrabold tracking-tighter text-navy text-[2.6rem] leading-[1.04] sm:text-6xl lg:text-[4.4rem]">
            <MaskedLine delay={0.25}>Enterprise-grade</MaskedLine>
            <MaskedLine delay={0.37}>finance leadership.</MaskedLine>
            <MaskedLine delay={0.49}>
              <span className="text-brand">AI-enabled.</span>
            </MaskedLine>
            <MaskedLine delay={0.61} className="font-medium text-navy/70 text-[1.9rem] sm:text-5xl lg:text-[3.2rem]">
              Right-sized for your business.
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subcopy"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease: EASE }}
            className="mt-8 max-w-xl text-base md:text-lg leading-relaxed text-body"
          >
            Fractional &amp; Virtual CFO leadership, Finance Shared Services,
            Controllership, Finance Transformation and customised AI tools for
            finance workflows — for high-growth companies, GCCs and overseas
            businesses building and scaling in India.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-book-consultation-btn"
              onClick={onConsult}
              className="group inline-flex items-center gap-2 rounded-full bg-brand px-8 py-4 text-sm font-bold text-navy shadow-[0_10px_30px_rgba(245,166,35,0.4)] transition-[transform,background-color,box-shadow] duration-300 hover:bg-brand-hover hover:text-white hover:scale-[1.03] active:scale-[0.98]"
            >
              Book a Consultation
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-explore-services-btn"
              href="#services"
              className="inline-flex items-center rounded-full border border-navy/20 px-8 py-4 text-sm font-bold text-navy transition-[border-color,background-color,transform] duration-300 hover:border-navy hover:bg-navy hover:text-white hover:scale-[1.03]"
            >
              Explore Our Services
            </a>
          </motion.div>

          <motion.ul
            data-testid="hero-proof-points"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-12 grid grid-cols-2 gap-x-6 gap-y-3 max-w-md"
          >
            {PROOF_POINTS.map((point, i) => (
              <motion.li
                key={point}
                initial={{ opacity: 0, x: -12 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6, delay: 1.25 + i * 0.1, ease: EASE }}
                className="flex items-center gap-2.5 text-sm font-semibold text-navy/80"
              >
                <span className="flex h-5 w-5 items-center justify-center rounded-full bg-brand/15">
                  <Check size={12} strokeWidth={3} className="text-brand-hover" />
                </span>
                {point}
              </motion.li>
            ))}
          </motion.ul>
        </div>

        <div ref={photoRef} className="relative w-full max-w-xl mx-auto lg:max-w-none">
          <motion.div
            style={isDesktop ? { y: yShape } : undefined}
            aria-hidden="true"
            className="absolute -inset-8 z-0"
          >
            <div className="absolute inset-0 rounded-[2.5rem] bg-navy rotate-3" />
            <div className="absolute -bottom-6 -left-6 h-40 w-40 rounded-full border-[3px] border-brand/70" />
          </motion.div>
          <motion.figure
            initial={{ opacity: 0, scale: 0.94, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease: EASE }}
            className="relative z-10 overflow-hidden rounded-[2rem] shadow-[0_32px_80px_rgba(10,31,60,0.25)]"
          >
            <motion.img
              style={isDesktop ? { y: yPhoto } : undefined}
              src="/assets/dashboard.webp"
              alt="Finance dashboard with KPIs, charts and management reporting — AI-enabled finance visibility delivered by Cogniti.ai CFO"
              className="aspect-[4/3] w-full scale-[1.1] object-cover object-[center_28%]"
            />
            <div className="absolute inset-0 bg-[linear-gradient(to_top,rgba(10,31,60,0.28),transparent_35%)]" />
          </motion.figure>
        </div>
      </div>
    </section>
  );
};
